import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaBrain, FaBolt, FaFlask, FaMicroscope, FaWater, FaHandRock,
  FaBed, FaDna, FaSyringe, FaChartLine, FaSearch, FaStethoscope,
  FaCheckCircle, FaArrowLeft, FaPhone,
} from 'react-icons/fa';
import { MdPsychology } from 'react-icons/md';
import { GiBrain } from 'react-icons/gi';

import service1 from '../assets/services/stroke-cerebrovascular.jpg';
import service2 from '../assets/services/epilepsy-seizures.jpg';
import service3 from '../assets/services/movement-disorders.jpg';
import service4 from '../assets/services/memory-dementia.jpg';
import service5 from '../assets/services/headache-migraine.jpg';
import service6 from '../assets/services/neuropathy.jpg';
import service7 from '../assets/services/sleep-disorders.jpg';
import service8 from '../assets/services/multiple-sclerosis.jpg';
import service9 from '../assets/services/neuro-infections.jpg';
import service10 from '../assets/services/eeg-evaluation.jpg';
import service11 from '../assets/services/ncs-emg.jpg';
import service12 from '../assets/services/neuro-critical-care.jpg';


const serviceDetails = [
  {
    slug: 'stroke-cerebrovascular',
    image: service1,
    icon: <GiBrain size={48} />,
    title: 'Stroke & Cerebrovascular',
    tagline: 'Fast diagnosis. Expert treatment. Better recovery.',
    overview: 'A stroke is a medical emergency where blood supply to part of the brain is cut off. At Naik Neuro Clinic, Dr. Rahul Naik provides rapid assessment, thrombolysis therapy when indicated, and a comprehensive post-stroke rehabilitation plan. Early intervention within the "golden hour" is critical to minimise brain damage.',
    whatWeTreat: [
      'Ischaemic Stroke (clot-based)',
      'Haemorrhagic Stroke (bleed-based)',
      'Transient Ischaemic Attack (TIA / mini-stroke)',
      'Cerebral Venous Sinus Thrombosis (CVST)',
      'Carotid Artery Disease',
      'Post-Stroke Rehabilitation Planning',
    ],
    howWeHelp: [
      'Urgent neurological assessment and CT/MRI review',
      'IV thrombolysis (clot-busting) therapy when eligible',
      'Secondary stroke prevention strategy',
      'Physiotherapy and rehabilitation co-ordination',
      'Caregiver and family counselling',
    ],
    whenToSee: 'Sudden face drooping, arm weakness, speech difficulty, sudden severe headache, or loss of vision — call emergency services immediately and contact us.',
    tags: ['Ischaemic Stroke', 'Haemorrhagic Stroke', 'TIA', 'Thrombolysis', 'CVST', 'Rehabilitation'],
  },
  {
    slug: 'epilepsy-seizures',
    image: service2,
    icon: <FaBolt size={44} />,
    title: 'Epilepsy & Seizures',
    tagline: 'Precise classification. Personalised control.',
    overview: 'Epilepsy is a neurological disorder marked by recurrent, unprovoked seizures. Dr. Naik uses EEG-guided evaluation and clinical assessment to precisely classify seizure type and design a long-term anti-epileptic management plan tailored to your lifestyle.',
    whatWeTreat: [
      'Focal (partial) seizures',
      'Generalised tonic-clonic seizures',
      'Absence seizures',
      'Breakthrough seizures on medication',
      'New-onset seizures in adults',
      'Juvenile Myoclonic Epilepsy (JME)',
    ],
    howWeHelp: [
      'EEG (routine, sleep, ambulatory) for seizure characterisation',
      'Anti-epileptic drug (AED) selection and optimisation',
      'Seizure diary monitoring and review',
      'Driving and lifestyle guidance',
      'Pre-conception counselling for women with epilepsy',
    ],
    whenToSee: 'Any first seizure, increase in seizure frequency, seizures despite medication, or concerns about anti-epileptic drug side effects.',
    tags: ['EEG Monitoring', 'AED Management', 'Focal Seizures', 'Generalised Epilepsy', 'JME'],
  },
  {
    slug: 'movement-disorders',
    image: service3,
    icon: <FaFlask size={44} />,
    title: 'Movement Disorders',
    tagline: "From Parkinson's to tremors — expert diagnosis and care.",
    overview: "Movement disorders affect the speed, fluency, quality, and ease of movement. Dr. Naik has specialised training in Parkinson's disease, essential tremors, dystonia, and other movement conditions, offering advanced diagnosis and evidence-based treatment protocols.",
    whatWeTreat: [
      "Parkinson's Disease",
      'Essential Tremor',
      'Dystonia',
      'Huntington\'s Disease',
      'Gait Disorders',
      'Drug-Induced Movement Disorders',
    ],
    howWeHelp: [
      'Detailed clinical assessment of motor symptoms',
      'Medication optimisation (Levodopa, dopamine agonists)',
      'Referral for DBS (Deep Brain Stimulation) evaluation',
      'Physiotherapy and occupational therapy co-ordination',
      'Caregiver training and support',
    ],
    whenToSee: "Resting tremor, stiffness, slowness of movement, balance problems, or changes in handwriting. Early diagnosis of Parkinson's leads to better outcomes.",
    tags: ["Parkinson's", 'Tremors', 'Dystonia', 'Gait Disorders', 'DBS Referral'],
  },
  {
    slug: 'memory-dementia',
    image: service4,
    icon: <MdPsychology size={48} />,
    title: 'Memory & Dementia',
    tagline: 'Compassionate cognitive care for patients and families.',
    overview: "Memory loss and cognitive decline can significantly impact quality of life. Dr. Naik provides comprehensive neuropsychological assessment, Alzheimer's disease diagnosis, and structured long-term memory care planning — with guidance for both patients and caregivers.",
    whatWeTreat: [
      "Alzheimer's Disease",
      'Vascular Dementia',
      'Lewy Body Dementia',
      'Mild Cognitive Impairment (MCI)',
      'Frontotemporal Dementia',
      'Reversible Causes of Memory Loss',
    ],
    howWeHelp: [
      'Formal cognitive testing (MMSE, MoCA)',
      'MRI brain review for atrophy patterns',
      'Blood tests to rule out reversible causes',
      'Medication management (Cholinesterase inhibitors)',
      'Caregiver education and coping strategies',
    ],
    whenToSee: 'Forgetfulness affecting daily life, confusion, getting lost in familiar places, personality changes, or difficulty with routine tasks.',
    tags: ["Alzheimer's", 'MCI', 'Vascular Dementia', 'Cognitive Testing', 'Caregiver Support'],
  },
  {
    slug: 'headache-migraine',
    image: service5,
    icon: <FaWater size={44} />,
    title: 'Headache & Migraine',
    tagline: 'Targeted treatment for long-term relief.',
    overview: 'Migraines and chronic headaches can be debilitating. Dr. Naik offers evidence-based diagnosis and comprehensive migraine management — from identifying triggers to building a personalised preventive and acute rescue plan that reduces frequency and severity.',
    whatWeTreat: [
      'Chronic Migraine (15+ days/month)',
      'Episodic Migraine with / without Aura',
      'Cluster Headaches',
      'Tension-Type Headache',
      'Medication Overuse Headache',
      'Secondary Headaches (requiring investigation)',
    ],
    howWeHelp: [
      'Headache diary analysis and trigger identification',
      'Acute rescue medication protocol',
      'Preventive therapy (Topiramate, Propranolol, Amitriptyline)',
      'Lifestyle and dietary modification guidance',
      'Referral for Botox injections when indicated',
    ],
    whenToSee: 'Headache occurring more than 4 days/month, sudden worst-ever headache, headache with fever/neck stiffness, or headache on waking.',
    tags: ['Migraine', 'Cluster Headache', 'Preventive Therapy', 'Trigger Management', 'Aura'],
  },
  {
    slug: 'neuropathy',
    image: service6,
    icon: <FaHandRock size={44} />,
    title: 'Neuropathy',
    tagline: 'Precise nerve assessment. Effective pain management.',
    overview: 'Peripheral neuropathy causes pain, numbness, tingling, and weakness in the hands and feet. Dr. Naik performs detailed nerve conduction studies (NCS) and EMG to pinpoint the type and severity of nerve damage and design an effective management plan.',
    whatWeTreat: [
      'Diabetic Peripheral Neuropathy',
      'Carpal Tunnel Syndrome',
      'Guillain-Barré Syndrome (GBS)',
      'Chronic Inflammatory Demyelinating Polyneuropathy (CIDP)',
      'Hereditary Neuropathies (CMT)',
      'Vitamin Deficiency Neuropathies',
    ],
    howWeHelp: [
      'Nerve Conduction Study (NCS) — in-clinic',
      'Electromyography (EMG) — in-clinic',
      'Blood tests for underlying cause',
      'Neuropathic pain management (Pregabalin, Duloxetine)',
      'Physiotherapy and splinting recommendations',
    ],
    whenToSee: 'Numbness, tingling, burning or electric pain in hands or feet, weakness in limbs, or difficulty walking.',
    tags: ['Diabetic Neuropathy', 'NCS/EMG', 'CIDP', 'GBS', 'Pain Management'],
  },
  {
    slug: 'sleep-disorders',
    image: service7,
    icon: <FaBed size={44} />,
    title: 'Sleep Disorders',
    tagline: 'Neurological sleep conditions diagnosed and managed.',
    overview: 'Many sleep disorders have an underlying neurological cause. Dr. Naik evaluates and manages conditions like narcolepsy, restless leg syndrome, and REM sleep behaviour disorder — conditions that are often missed or misdiagnosed.',
    whatWeTreat: [
      'Narcolepsy with Cataplexy',
      'Restless Leg Syndrome (RLS)',
      'REM Sleep Behaviour Disorder (RBD)',
      'Idiopathic Hypersomnia',
      'Sleep-Related Epilepsy',
      'Insomnia with Neurological Cause',
    ],
    howWeHelp: [
      'Detailed sleep history and Epworth Sleepiness Scale',
      'EEG sleep study guidance',
      'Polysomnography referral when required',
      'Medication management (Clonazepam, Modafinil, Pramipexole)',
      'Sleep hygiene counselling',
    ],
    whenToSee: 'Excessive daytime sleepiness, acting out dreams, uncontrollable urge to move legs at night, or sudden muscle weakness triggered by emotion.',
    tags: ['Narcolepsy', 'Restless Legs', 'REM Disorder', 'Hypersomnia', 'Sleep EEG'],
  },
  {
    slug: 'multiple-sclerosis',
    image: service8,
    icon: <FaDna size={44} />,
    title: 'Multiple Sclerosis',
    tagline: 'Expert MS diagnosis and long-term monitoring.',
    overview: 'Multiple Sclerosis (MS) is a chronic autoimmune condition affecting the brain and spinal cord. Dr. Naik uses MRI and clinical criteria for accurate diagnosis, initiates disease-modifying therapy (DMT), and provides long-term neurological monitoring and relapse management.',
    whatWeTreat: [
      'Relapsing-Remitting MS (RRMS)',
      'Secondary Progressive MS (SPMS)',
      'Clinically Isolated Syndrome (CIS)',
      'Neuromyelitis Optica (NMO)',
      'MS Relapses',
      'MS-related Fatigue and Pain',
    ],
    howWeHelp: [
      'MRI brain and spine for McDonald criteria diagnosis',
      'Disease-Modifying Therapy (DMT) initiation and monitoring',
      'IV methylprednisolone for acute relapses',
      'Symptomatic management (fatigue, spasticity, pain)',
      'Coordination with physiotherapy and ophthalmology',
    ],
    whenToSee: 'Episodes of vision loss, double vision, limb weakness, sensory disturbance, or balance problems — especially if recurring.',
    tags: ['RRMS', 'Disease-Modifying Therapy', 'MRI Monitoring', 'Relapse Treatment', 'NMO'],
  },
  {
    slug: 'neuro-infections',
    image: service9,
    icon: <FaSyringe size={44} />,
    title: 'Neuro-infections',
    tagline: 'Urgent diagnosis and targeted treatment for CNS infections.',
    overview: 'Infections of the brain and nervous system — meningitis, encephalitis, and brain abscesses — can be life-threatening. Dr. Naik provides urgent clinical evaluation, CSF analysis guidance, and targeted antibiotic/antiviral therapy for optimal outcomes.',
    whatWeTreat: [
      'Bacterial Meningitis',
      'Viral Meningitis and Encephalitis',
      'Tubercular Meningitis (TBM)',
      'Brain Abscess',
      'Neurocysticercosis',
      'HIV-related Neurological Complications',
    ],
    howWeHelp: [
      'Emergency neurological assessment',
      'CSF (cerebrospinal fluid) analysis interpretation',
      'MRI brain for infection localisation',
      'Empiric and targeted antimicrobial therapy',
      'Long-term follow-up and complication monitoring',
    ],
    whenToSee: 'Sudden severe headache, fever with neck stiffness, photophobia, confusion, seizures, or altered consciousness. This is a neurological emergency.',
    tags: ['Meningitis', 'Encephalitis', 'TBM', 'Neurocysticercosis', 'CSF Analysis'],
  },
  {
    slug: 'eeg-evaluation',
    image: service10,
    icon: <FaChartLine size={44} />,
    title: 'EEG Evaluation',
    tagline: 'In-clinic EEG — no referrals, no delays.',
    overview: "Electroencephalography (EEG) records electrical activity in the brain. At Naik Neuro Clinic, EEG is performed in-clinic and personally interpreted by Dr. Naik. It's a vital tool for diagnosing epilepsy, evaluating encephalopathy, and investigating unexplained neurological symptoms.",
    whatWeTreat: [
      'Seizure Characterisation and Classification',
      'Encephalopathy Evaluation',
      'Sleep-Related Epilepsy',
      'Functional Neurological Disorder',
      'Post-Cardiac Arrest Brain Activity',
      'Neurological Workup for Unexplained Symptoms',
    ],
    howWeHelp: [
      'Routine EEG (awake and drowsy)',
      'Sleep-deprived EEG for better seizure detection',
      'Ambulatory (24-hour) EEG when required',
      'Same-visit interpretation by Dr. Naik',
      'Report correlated with clinical history for precise diagnosis',
    ],
    whenToSee: 'Suspected seizures, unexplained blackouts, episodes of confusion, abnormal movements, or as part of a neurological workup.',
    tags: ['Routine EEG', 'Sleep EEG', 'Ambulatory EEG', 'Seizure Diagnosis', 'Encephalopathy'],
  },
  {
    slug: 'ncs-emg',
    image: service11,
    icon: <FaSearch size={44} />,
    title: 'NCS / EMG',
    tagline: 'Detailed nerve and muscle studies — in-clinic.',
    overview: 'Nerve Conduction Studies (NCS) and Electromyography (EMG) are gold-standard tests for evaluating nerve and muscle disorders. Performed and interpreted in-clinic by Dr. Naik, these tests provide precise information about peripheral nerve function and muscle health.',
    whatWeTreat: [
      'Peripheral Neuropathy',
      'Carpal Tunnel and Entrapment Neuropathies',
      'Radiculopathy (nerve root compression)',
      'Neuromuscular Junction Disorders (Myasthenia Gravis)',
      'Myopathy (muscle disease)',
      'Plexopathy',
    ],
    howWeHelp: [
      'Motor and Sensory Nerve Conduction Studies',
      'Needle Electromyography (Needle EMG)',
      'Repetitive Nerve Stimulation for NMJ disorders',
      'Correlation with clinical symptoms for diagnosis',
      'Treatment planning based on NCS/EMG findings',
    ],
    whenToSee: 'Numbness, tingling, weakness in arms or legs, suspected carpal tunnel syndrome, muscle wasting, or for pre-surgical nerve evaluation.',
    tags: ['Motor NCS', 'Sensory NCS', 'Needle EMG', 'Carpal Tunnel', 'Myasthenia Gravis'],
  },
  {
    slug: 'neuro-critical-care',
    image: service12,
    icon: <FaStethoscope size={44} />,
    title: 'Neuro-critical Care',
    tagline: 'Expert management of acute neurological emergencies.',
    overview: 'Neurological emergencies require immediate, specialised intervention. Dr. Naik provides expert management of acute conditions including status epilepticus, acute encephalopathy, and Guillain-Barré syndrome — with rapid assessment and evidence-based treatment.',
    whatWeTreat: [
      'Status Epilepticus',
      'Acute Encephalopathy',
      'Guillain-Barré Syndrome (GBS)',
      'Critical Illness Neuropathy/Myopathy',
      'Acute CNS Infections (Meningitis/Encephalitis)',
      'Neuromuscular Respiratory Failure',
    ],
    howWeHelp: [
      'Rapid neurological assessment in acute settings',
      'IV anti-epileptic therapy for status epilepticus',
      'IVIG / Plasmapheresis coordination for GBS and CIDP',
      'ICU neurology consultation',
      'Rehabilitation planning after acute recovery',
    ],
    whenToSee: 'Prolonged seizure (>5 minutes), sudden confusion, rapidly worsening weakness, difficulty breathing, or any sudden severe neurological symptom. Seek emergency care immediately.',
    tags: ['Status Epilepticus', 'GBS', 'Encephalopathy', 'ICU Neurology', 'IVIG'],
  },
];

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const service = serviceDetails.find(s => s.slug === slug);

  if (!service) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px' }}>
        <h2>Service not found</h2>
        <button className="btn-primary" style={{ marginTop: 24 }} onClick={() => navigate('/services')}>
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <div className="service-detail-page">

      {/* ── PAGE HERO ── */}
      <section className="page-hero">
        <div className="page-hero-bg-dots" />
        <div className="container">
          <div className="page-hero-content">
            <button className="sd-back-btn" onClick={() => navigate('/services')}>
              <FaArrowLeft size={13} style={{ marginRight: 7 }} />
              All Services
            </button>
            <div className="sd-hero-icon">{service.icon}</div>
            <h1 className="page-hero-title">{service.title}</h1>
            <p className="page-hero-sub">{service.tagline}</p>
          </div>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <section className="sd-content-section">
        <div className="container">
          <div className="sd-content-grid">

            {/* LEFT — main content */}
            <div className="sd-main">

              {/* ── SERVICE IMAGE ── */}
              <div className="sd-service-image-wrap">
                {service.image ? (
                  <img
                    src={service.image}
                    alt={`${service.title} illustration`}
                    className="sd-service-image"
                  />
                ) : (
                  <div className="sd-service-image-placeholder">
                    <div className="sd-service-image-placeholder-icon">{service.icon}</div>
                    <span className="sd-service-image-placeholder-text">{service.title}</span>
                  </div>
                )}
              </div>

              <div className="sd-block">
                <h2 className="sd-block-title">Overview</h2>
                <p className="sd-block-text">{service.overview}</p>
              </div>

              <div className="sd-block">
                <h2 className="sd-block-title">What We Treat</h2>
                <ul className="sd-checklist">
                  {service.whatWeTreat.map(item => (
                    <li key={item} className="sd-check-item">
                      <FaCheckCircle size={15} className="sd-check-icon" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sd-block">
                <h2 className="sd-block-title">How We Help</h2>
                <ul className="sd-checklist">
                  {service.howWeHelp.map(item => (
                    <li key={item} className="sd-check-item">
                      <FaCheckCircle size={15} className="sd-check-icon" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sd-when-card">
                <div className="sd-when-label">⏰ When to See a Neurologist</div>
                <p className="sd-when-text">{service.whenToSee}</p>
              </div>

              <div className="sd-tags-row">
                {service.tags.map(tag => (
                  <span key={tag} className="service-tag">{tag}</span>
                ))}
              </div>
            </div>

            {/* RIGHT — sidebar */}
            <div className="sd-sidebar">

              <div className="sd-sidebar-card">
                <div className="sd-sidebar-icon-wrap">{service.icon}</div>
                <h3 className="sd-sidebar-title">{service.title}</h3>
                <p className="sd-sidebar-sub">Treated by Dr. Rahul Naik<br/>DM Neurology · AIIMS</p>
                <button className="btn-primary sd-sidebar-btn" onClick={() => navigate('/contact')}>
                  Book Appointment
                </button>
                
              </div>

              <div className="sd-sidebar-info">
                <div className="sd-info-row">
                  <span className="sd-info-icon">🎓</span>
                  <div>
                    <div className="sd-info-label">Treated By</div>
                    <div className="sd-info-val">Dr. Rahul Naik, DM Neurology (AIIMS)</div>
                  </div>
                </div>
                <div className="sd-info-row">
                  <span className="sd-info-icon">🏥</span>
                  <div>
                    <div className="sd-info-label">Clinic</div>
                    <div className="sd-info-val">Naik Neuro Clinic, Nashik</div>
                  </div>
                </div>
                <div className="sd-info-row">
                  <span className="sd-info-icon">🕐</span>
                  <div>
                    <div className="sd-info-label">OPD Hours</div>
                    <div className="sd-info-val">Mon–Sat: 10am–2pm, 5pm–8pm</div>
                  </div>
                </div>
              </div>

              <div className="sd-other-services">
                <h4 className="sd-other-title">Other Services</h4>
                {serviceDetails.filter(s => s.slug !== slug).slice(0, 5).map(s => (
                  <button
                    key={s.slug}
                    className="sd-other-item"
                    onClick={() => { navigate(`/services/${s.slug}`); window.scrollTo({ top: 0 }); }}
                  >
                    <span>{s.title}</span>
                    <span className="sd-other-arrow">→</span>
                  </button>
                ))}
                <button className="sd-view-all" onClick={() => navigate('/services')}>
                  View All Services →
                </button>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-text">
              <h2 className="cta-title">Concerned About {service.title}?</h2>
              <p className="cta-sub">Consult Dr. Rahul Naik — AIIMS-trained DM Neurologist — for expert diagnosis and care.</p>
            </div>
            <div className="cta-actions">
              <button className="cta-btn-primary" onClick={() => navigate('/contact')}>Book Appointment</button>
              <button className="cta-btn-secondary" onClick={() => navigate('/services')}>All Services</button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ServiceDetail;

